import { useMemo, useState } from 'react'
import { Alert, Card, Col, DatePicker, Row, Space, Statistic, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BankOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import {
  fetchBranchReport,
  downloadFinanceExport,
  type BranchAggregate,
  type BranchReportRow,
} from '@/features/finance/financeApi'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { fetchOrgNodeTypes, orgTypeName, typeOf } from '@/features/org/orgApi'
import { matchesSearch, useLocalListState } from '@/shared/lib/localList'
import { defaultDayRange } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

/**
 * Filial hisoboti — jamlovchi P&L (`GET /api/finance/reports/branches`).
 *
 * <p>Mijoz hujjatining §5.5 bandlari «Filiallarni taqqoslash»,
 * «Kompaniya KPI» va «Bosh ofis jamlovchi ko'rinishi» shu bitta
 * sahifada. Ilgari bu ma'lumot faqat EKSPORT (fayl) sifatida ochilardi
 * — panelda ko'radigan ekran yo'q edi (`docs/11` §3, §5.5).
 *
 * ⚠️ Jadval DARAXT bo'ylab yig'ilgan: qatorlarni pastga qo'shmang, ota
 * qator bolasini qamrab oladi. Umumiy son «Kompaniya» KPI blokida.
 * «Taqsimlanmagan» qismi ATAYLAB alohida ko'rsatiladi (nolga
 * aylantirilmaydi) — sabab ma'lumotning holatida (`docs/11` §3).
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * `FinanceReportController#branches` faqat `from`/`to` ni oladi:
 * `page`, `size`, `sort` YO'Q. Javob to'liq keladi va uning uzunligi
 * xarajat markazlari soniga teng (`costCenterCount` KPI si aynan shu).
 *
 * <h2>⚠️ SARALASH YO'Q — TARTIB MA'NO TASHIYDI</h2>
 * Qatorlar daraxt bo'ylab, `depth` bilan surib chiziladi. Ularni
 * masalan daromad bo'yicha saralash otani bolasidan ajratib qo'yardi va
 * chekinish (`paddingLeft`) ma'nosiz bo'lib qolardi — ekranda esa u
 * baribir iyerarxiyaga o'xshab ko'rinardi. Shuning uchun ustunlarda
 * `sorter` YO'Q va u ataylab.
 *
 * <h2>⚠️ `created desc` QO'LLANMAYDI</h2>
 * `BranchReportRow` da sana maydoni yo'q va bo'lishi ham mumkin emas:
 * qator — tashkiliy tugun bo'yicha AGREGAT, yozuv emas.
 */
export function FinanceBranchReportPage() {
  const { t } = useTranslation()
  const filter = useLocalListState()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => {
    // Oy boshidan bugungacha — filial hisoboti odatda joriy oy uchun ochiladi.
    return [dayjs(defaultDayRange(0)[1]).startOf('month'), defaultDayRange(0)[1]]
  })

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.finance.branchReport(from, to),
    queryFn: ({ signal }) => fetchBranchReport({ from, to }, signal),
  })

  const currency = report.data?.note.currency ?? ''
  const company = report.data?.company
  const unallocated = report.data?.unallocated

  const rows = useMemo(() => report.data?.rows ?? [], [report.data])
  const nodeTypes = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    staleTime: 5 * 60_000,
  })
  const kindName = (kind: string) => {
    const type = typeOf(nodeTypes.data ?? [], kind)
    return type ? orgTypeName(type, t) : null
  }
  /**
   * ⚠️ FILTR QATORLARNI OLIB TASHLAYDI, DARAXTNI QAYTA QURMAYDI.
   *
   * Mos kelgan tugunning otasi filtrga tushmasligi mumkin — u holda
   * chekinish (`depth`) «osilgan» bo'lib ko'rinadi. Shuning uchun filtr
   * faol bo'lganda jadval ostida buning sababi yoziladi; qator
   * qiymatlarining O'ZI o'zgarmaydi (ular baribir agregat).
   */
  const visible = useMemo(
    () => rows.filter((row) => matchesSearch(filter.search, row.nodeName, row.nodeCode, row.nodeKind)),
    [rows, filter.search],
  )

  const margin = useMemo(() => marginPercent(company), [company])
  const revenueDelta = useMemo(
    () => percentChange(company?.revenueMinor, company?.previousRevenueMinor),
    [company],
  )
  const profitDelta = useMemo(
    () => percentChange(company?.netProfitMinor, company?.previousNetProfitMinor),
    [company],
  )

  const columns: ColumnsType<BranchReportRow> = [
    {
      key: 'node',
      title: t('finance.branchReport.columns.node'),
      width: 300,
      render: (_, row) => (
        <Space size={4}>
          {/* Chuqurlik bo'yicha chapga surish — daraxt shakli ko'rinsin. */}
          <span style={{ paddingInlineStart: Math.max(0, row.depth - 1) * 18 }}>
            <Typography.Text strong={row.depth <= 1}>{row.nodeName}</Typography.Text>
          </span>
          {kindName(row.nodeKind) ? <Tag>{kindName(row.nodeKind)}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'revenue',
      align: 'right',
      width: 160,
      title: t('finance.branchReport.columns.revenue'),
      // ⚠️ `0,00` — O'LCHANGAN NOL: server har tugunda `revenueMinor` ni
      // SON sifatida beradi (`branchAggregateSchema`, ixtiyoriy emas).
      // Chiziqcha bu yerda «ma'lumot yo'q» degan BOSHQA xabar bo'lardi.
      render: (_, row) => formatMoneyValue(money(row.revenueMinor, currency)),
    },
    {
      key: 'cogs',
      align: 'right',
      width: 150,
      title: t('finance.branchReport.columns.cogs'),
      render: (_, row) => formatMoneyValue(money(row.cogsMinor, currency)),
    },
    {
      key: 'opex',
      align: 'right',
      width: 160,
      title: t('finance.branchReport.columns.opex'),
      render: (_, row) =>
        formatMoneyValue(money(row.operatingExpenseMinor + row.otherExpenseMinor, currency)),
    },
    {
      key: 'net',
      align: 'right',
      width: 170,
      title: t('finance.branchReport.columns.net'),
      render: (_, row) => (
        <Typography.Text type={row.netProfitMinor < 0 ? 'danger' : undefined} strong>
          {formatMoneyValue(money(row.netProfitMinor, currency))}
        </Typography.Text>
      ),
    },
    {
      key: 'delta',
      align: 'right',
      width: 130,
      title: t('finance.branchReport.columns.vsPrevious'),
      render: (_, row) => <DeltaText value={percentChange(row.netProfitMinor, row.previousNetProfitMinor)} t={t} />,
    },
  ]

  return (
    <>
      <PageHeader
        title={t('finance.branchReport.title')}
        description={t('finance.branchReport.intro')}
        extra={
          <ServerExportButton
            size="middle"
            title={t('finance.branchReport.title')}
            disabled={report.isError}
            run={(format, onProgress) =>
              downloadFinanceExport('branches', format, { from, to, onProgress })
            }
          />
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('finance.branchReport.rangeLabel')}:</Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
        </Space>
        {report.data ? (
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('finance.branchReport.windowNote', {
                from: report.data.note.from,
                to: report.data.note.toInclusive,
              })}
              {' · '}
              {t('finance.branchReport.periodsNote', {
                periods:
                  report.data.note.periods
                    .map((p) => `${p.code} (${t(`periodState.${p.state}`, { defaultValue: p.state })})`)
                    .join(', ') ||
                  t('finance.branchReport.noPeriods'),
              })}
            </Typography.Text>
          </div>
        ) : null}
      </Card>

      {/*
        ⚠️ Doira cheklovi — ochiq ogohlantirish, yashirin emas. Reseller
        yoki filial boshlig'i kompaniya KPI sini ko'rmasligi mumkin
        (`scopeRestricted`) va bu holatda «bo'sh» son «nol» degani EMAS.
      */}
      {report.data?.note.scopeRestricted ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.branchReport.scopeRestricted')}
        />
      ) : null}

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          {/* --- Kompaniya KPI (jamlovchi) --- */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.branchReport.kpi.revenue')}
                  value={formatMoneyValue(money(company?.revenueMinor ?? 0, currency))}
                />
                <DeltaText value={revenueDelta} t={t} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.branchReport.kpi.netProfit')}
                  valueStyle={
                    (company?.netProfitMinor ?? 0) < 0 ? { color: '#cf1322' } : undefined
                  }
                  value={formatMoneyValue(money(company?.netProfitMinor ?? 0, currency))}
                />
                <DeltaText value={profitDelta} t={t} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.branchReport.kpi.margin')}
                  value={margin === null ? '—' : `${margin.toFixed(1)}%`}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.branchReport.kpi.costCenters')}
                  value={formatNumber(report.data?.costCenterCount ?? 0)}
                />
              </Card>
            </Col>
          </Row>

          {/* --- Filiallar daraxti --- */}
          <Card
            title={t('finance.branchReport.tableTitle')}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {/*
              ⚠️ «Ota bolasini qamrab oladi» — EKRANDA, izohda emas.
              Bu qoida faqat kodda yozilgan edi va operator jadvalni
              o'zi qo'shib ko'rib, «kompaniya yakuni noto'g'ri» degan
              xato xulosaga kelishi mumkin edi.
            */}
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              {t('finance.branchReport.parentIncludesChildren')}
            </Typography.Paragraph>

            <ReportFilterBar
              filter={filter}
              searchPlaceholder={t('finance.branchReport.searchPlaceholder')}
              shown={visible.length}
              loaded={rows.length}
              totalsNote={t('finance.branchReport.filterNote')}
            />

            <Table<BranchReportRow>
              rowKey="nodeId"
              columns={columns}
              dataSource={visible}
              loading={report.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
              locale={{
                emptyText: (
                  <ReportTableEmpty
                    filter={filter}
                    icon={<BankOutlined />}
                    title={t('finance.branchReport.empty')}
                    description={t('finance.branchReport.emptyHint')}
                  />
                ),
              }}
            />

            {/*
              ⚠️ «Taqsimlanmagan» qatori jadvaldan TASHQARIDA — u daraxt
              tuguni emas, hech qaysi filialga biriktirilmagan qism. Uni
              qatorlar ichiga qo'shsak «filiallar yig'indisi = kompaniya»
              tengligi buzilardi.
            */}
            {unallocated && unallocated.lineCount > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={t('finance.branchReport.unallocated', {
                  amount: formatMoneyValue(money(unallocated.netProfitMinor, currency)),
                })}
                description={t('finance.branchReport.unallocatedHint')}
              />
            ) : null}
          </Card>
        </>
      )}
    </>
  )
}

/** Foyda marjasi (%) — daromad nol bo'lsa `null` (0% EMAS). */
function marginPercent(agg: BranchAggregate | undefined): number | null {
  if (!agg || agg.revenueMinor === 0) return null
  return (agg.netProfitMinor / agg.revenueMinor) * 100
}

/**
 * Oldingi teng oynaga nisbatan o'zgarish (%).
 * ⚠️ Oldingi qiymat nol bo'lsa `null` — «cheksiz o'sish» ni son bilan
 * ko'rsatib bo'lmaydi va «0%» yolg'on bo'lardi.
 */
function percentChange(current?: number, previous?: number): number | null {
  if (current === undefined || previous === undefined || previous === 0) return null
  return ((current - previous) / Math.abs(previous)) * 100
}

function DeltaText({
  value,
  t,
}: {
  value: number | null
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  if (value === null) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('finance.branchReport.noComparison')}
      </Typography.Text>
    )
  }
  const positive = value >= 0
  return (
    <Typography.Text type={positive ? 'success' : 'danger'} style={{ fontSize: 12 }}>
      {positive ? '▲' : '▼'} {Math.abs(value).toFixed(1)}%
    </Typography.Text>
  )
}
